import { Component, computed, ElementRef, inject, input, signal } from '@angular/core';
import { generateId } from '@sb/shared/util';
import { clsx } from 'clsx';

import type { AnchoredPlacement } from '../anchored-overlay';
import { toGapEdge, type DisclosureFlyoutGapEdge } from './disclosure-flyout-placement';

/**
 * How far the surface stands off its trigger. `wide` also clears padding the trigger sits inside,
 * such as a side nav's own inset, so the surface lands visibly outside that container.
 */
export type DisclosureFlyoutGap = 'default' | 'wide';

// Every gap class, spelled out in full so Tailwind's scanner sees each one it has to emit.
const GAP_CLASSES: Record<DisclosureFlyoutGapEdge, Record<DisclosureFlyoutGap, string>> = {
  'inline-start': { default: 'ps-xs', wide: 'ps-md' },
  'inline-end': { default: 'pe-xs', wide: 'pe-md' },
  top: { default: 'pt-xs', wide: 'pt-md' },
  bottom: { default: 'pb-xs', wide: 'pb-md' },
};

/**
 * Floating surface revealed by an `[sbDisclosureFlyoutTriggerFor]`.
 *
 * The gap between the panel and its trigger is padding on this element rather than a positioning
 * offset, so the two boxes touch and a pointer crossing the gap never leaves both at once. WCAG
 * 1.4.13 Hoverable requires the pointer to reach the revealed content, and a positioning offset
 * puts dead space in the way.
 */
@Component({
  selector: 'sb-disclosure-flyout-panel',
  template: `<div [class]="surfaceClasses">
    <ng-content />
  </div>`,
  host: {
    '[class]': 'hostClasses()',
    '[id]': 'id()',
    '[hidden]': '!rendered()',
    '(mouseenter)': 'pointerInside.set(true)',
    '(mouseleave)': 'pointerInside.set(false)',
    '(focusin)': 'focusInside.set(true)',
    '(focusout)': 'onFocusOut($event)',
  },
})
export class DisclosureFlyoutPanelComponent {
  element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  id = input<string>(generateId('sb-disclosure-flyout-panel'));

  /** Distance from the trigger to the visible surface. Still padding, so the pointer path stays unbroken. */
  gap = input<DisclosureFlyoutGap>('default');

  #expanded = signal(false);
  #placement = signal<AnchoredPlacement>('inline-end-top');
  #hosted = signal(false);

  /** Both driven by the trigger, through `setExpanded`/`setPlacement`. */
  expanded = this.#expanded.asReadonly();
  placement = this.#placement.asReadonly();

  /** Stays true after a collapse for as long as the panel is still playing its way out. */
  rendered = computed(() => this.#expanded() || this.#hosted());

  /** Read by the trigger, which owns the decision to expand or collapse. */
  pointerInside = signal(false);
  focusInside = signal(false);

  surfaceClasses = clsx(
    'rounded-xl border border-solid border-overlay bg-overlay py-xs shadow-lg',
    'flex min-w-48 flex-col overflow-y-auto text-default',
  );

  hostClasses = computed(() =>
    clsx(
      'z-dropdown max-h-dvh',
      // Only while rendered: `display` from a class beats the `hidden` attribute's UA rule, so a
      // permanent `flex` here would render every collapsed panel.
      this.rendered() && 'flex',
      GAP_CLASSES[toGapEdge(this.#placement())][this.gap()],
    ),
  );

  setExpanded(expanded: boolean): void {
    this.#expanded.set(expanded);
  }

  setPlacement(placement: AnchoredPlacement): void {
    this.#placement.set(placement);
  }

  setHosted(hosted: boolean): void {
    this.#hosted.set(hosted);
  }

  onFocusOut(focusEvent: FocusEvent): void {
    const nextTarget = focusEvent.relatedTarget;

    // Moving between two links inside the panel is not focus leaving it.
    if (nextTarget instanceof Node && this.element.contains(nextTarget)) {
      return;
    }

    this.focusInside.set(false);
  }
}
